import React, { useState } from 'react';
import { View, Col } from '@amzn/stencil-react-components/layout';
import { Label } from '@amzn/stencil-react-components/text';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Modal, ModalContent, ModalStyle } from '@amzn/stencil-react-components/modal';
import { Radio } from '@amzn/stencil-react-components/radio';
import { useTranslation } from 'react-i18next';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import TranslationKeys from 'src/constants/translation-keys';
import { recordEvent } from 'src/analytics/amplify';

enum PreboarderIntent {
    GET_READY = 'get_ready',
    REVIEW_OFFER = 'review_offer',
}

interface Props {
    screenName: string;
    children: (props: { open: () => void }) => React.ReactNode;
}

export default function PreboarderIntentFlyout({ screenName, children }: Props) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIntent, setSelectedIntent] = useState<PreboarderIntent>(PreboarderIntent.GET_READY);

    const open = () => {
        setSelectedIntent(PreboarderIntent.GET_READY);
        setIsOpen(true);
    };
    const close = () => setIsOpen(false);

    const handleContinue = () => {
        recordEvent({
            name: `${screenName}.PreboarderIntent.${selectedIntent}.Count`,
            immediate: true
        });
        close();
        if (selectedIntent === PreboarderIntent.GET_READY) {
            window.location.href = '{{PbAuthenticationControllerUrl}}';
        } else {
            window.location.href = '{{JobseekerAuthenticationControllerUrl}}';
        }
    };

    return (
        <>
            {children({ open })}
            <Modal isOpen={isOpen} close={close} style={ModalStyle.Default}>
                <ModalContent
                    titleText={t(TranslationKeys.UI.PREBOARDER_INTENT.TITLE)}
                >
                    <Col gridGap="S200">
                        <View
                            style={{
                                border: selectedIntent === PreboarderIntent.GET_READY
                                    ? '2px solid var(--st-dt-color-action-primary-bg-default)'
                                    : `1px solid ${token('color.border.primary')}`,
                                borderRadius: '8px',
                                padding: '12px',
                                cursor: 'pointer',
                            }}
                            onClick={() => setSelectedIntent(PreboarderIntent.GET_READY)}
                        >
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label style={{ fontWeight: 700, fontSize: '14px', color: token('color.text.primary') }}>
                                    {t(TranslationKeys.UI.PREBOARDER_INTENT.GET_READY_HEADER)}
                                </Label>
                                <Radio
                                    name="preboarder-intent"
                                    checked={selectedIntent === PreboarderIntent.GET_READY}
                                    onChange={() => setSelectedIntent(PreboarderIntent.GET_READY)}
                                />
                            </View>
                            <Label style={{ fontSize: '13px', color: token('color.text.secondary'), marginTop: '4px' }}>
                                {t(TranslationKeys.UI.PREBOARDER_INTENT.GET_READY_DESCRIPTION)}
                            </Label>
                        </View>
                        <View
                            style={{
                                border: selectedIntent === PreboarderIntent.REVIEW_OFFER
                                    ? '2px solid var(--st-dt-color-action-primary-bg-default)'
                                    : `1px solid ${token('color.border.primary')}`,
                                borderRadius: '8px',
                                padding: '12px',
                                cursor: 'pointer',
                            }}
                            onClick={() => setSelectedIntent(PreboarderIntent.REVIEW_OFFER)}
                        >
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label style={{ fontWeight: 700, fontSize: '14px', color: token('color.text.primary') }}>
                                    {t(TranslationKeys.UI.PREBOARDER_INTENT.REVIEW_OFFER_HEADER)}
                                </Label>
                                <Radio
                                    name="preboarder-intent"
                                    checked={selectedIntent === PreboarderIntent.REVIEW_OFFER}
                                    onChange={() => setSelectedIntent(PreboarderIntent.REVIEW_OFFER)}
                                />
                            </View>
                            <Label style={{ fontSize: '13px', color: token('color.text.secondary'), marginTop: '4px' }}>
                                {t(TranslationKeys.UI.PREBOARDER_INTENT.REVIEW_OFFER_DESCRIPTION)}
                            </Label>
                        </View>
                    </Col>
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                        <Button
                            variant={ButtonVariant.Tertiary}
                            onClick={close}
                            style={{ border: `1px solid ${token('color.border.primary')}`, minWidth: '120px', borderRadius: '24px' }}
                        >
                            {t(TranslationKeys.UI.BUTTONS.CANCEL)}
                        </Button>
                        <Button
                            variant={ButtonVariant.Primary}
                            onClick={handleContinue}
                            style={{ minWidth: '120px', borderRadius: '24px' }}
                        >
                            {t(TranslationKeys.UI.BUTTONS.CONTINUE)}
                        </Button>
                    </View>
                </ModalContent>
            </Modal>
        </>
    );
}
